<template>
  <div class="task-system-modal">
    <!-- 任务工作台状态栏 -->
    <div class="task-workbench-head">
      <div class="task-workbench-title">
        <span class="task-workbench-kicker">洞府事务</span>
        <strong>任务中心</strong>
      </div>
      <div class="task-workbench-stats">
        <span class="task-stat task-stat--all"><small>全部</small><b>{{ totalTaskCount }}</b></span>
        <span class="task-stat task-stat--active"><small>进行中</small><b>{{ uncompletedTaskCount }}</b></span>
        <span class="task-stat task-stat--ready"><small>已完成</small><b>{{ completedTaskCount }}</b></span>
      </div>
      <Button class="task-workbench-refresh" size="small" type="primary" icon="md-refresh" @click="refreshAllTasks">刷新任务</Button>
    </div>

    <!-- Tab页签 - 放在顶部 -->
    <div class="top-tabs" v-if="showTopTabs">
      <Tabs v-model="activeTab" class="task-tabs-top" size="small">
        <TabPane label="常规任务" name="normal" icon="md-list">
          <!-- 常规任务内容 -->
        </TabPane>
        <TabPane label="悬赏任务" name="reward" icon="md-clipboard">
          <!-- 悬赏任务内容 -->
        </TabPane>
      </Tabs>
    </div>

    <div class="task-container-modal">
      <!-- 常规任务内容 - PC端保持Row/Col布局 -->
      <div v-if="showNormalTab && activeTab === 'normal'" class="tab-pane normal-task-pane">
        <!-- 移动端分类按钮（只在移动端显示） -->
        <div class="mobile-category-buttons">
          <div class="category-scroll">
            <Button
              :type="activeCategory === 'main' ? 'primary' : 'default'"
              class="category-btn"
              @click="switchCategory('main')"
              size="small"
            >
              <Icon type="md-alert" />
              主线
            </Button>
            <Button
              :type="activeCategory === 'branch' ? 'primary' : 'default'"
              class="category-btn"
              @click="switchCategory('branch')"
              size="small"
            >
              <Icon type="ios-alert" />
              支线
            </Button>
            <Button
              :type="activeCategory === 'daily' ? 'primary' : 'default'"
              class="category-btn"
              @click="switchCategory('daily')"
              size="small"
            >
              <Icon type="ios-ionic-outline" />
              日常
            </Button>
            <Button
              :type="activeCategory === 'sect' ? 'primary' : 'default'"
              class="category-btn"
              @click="switchCategory('sect')"
              size="small"
            >
              <Icon type="ios-map-outline" />
              宗门
            </Button>
          </div>
        </div>

        <Row :gutter="24" class="task-layout-row">
          <!-- 左侧任务分类和列表 -->
          <Col span="8" class="task-left-column" :class="{ 'mobile-hidden': isMobile && showDetail }">
            <Card class="task-sidebar-card" :bordered="false">
              <!-- PC端任务分类按钮 -->
              <div class="task-category-buttons pc-category">
                <div class="category-row">
                  <Button
                    :type="activeCategory === 'main' ? 'primary' : 'default'"
                    class="category-btn"
                    @click="switchCategory('main')"
                    size="small"
                  >
                    <Icon type="md-alert" />
                    主线任务
                  </Button>
                  <Button
                    :type="activeCategory === 'branch' ? 'primary' : 'default'"
                    class="category-btn"
                    @click="switchCategory('branch')"
                    size="small"
                  >
                    <Icon type="ios-alert" />
                    支线任务
                  </Button>
                  <Button
                    :type="activeCategory === 'daily' ? 'primary' : 'default'"
                    class="category-btn"
                    @click="switchCategory('daily')"
                    size="small"
                  >
                    <Icon type="ios-ionic-outline" />
                    日常任务
                  </Button>
                  <Button
                    :type="activeCategory === 'sect' ? 'primary' : 'default'"
                    class="category-btn"
                    @click="switchCategory('sect')"
                    size="small"
                  >
                    <Icon type="ios-map-outline" />
                    宗门任务
                  </Button>
                </div>
              </div>

              <!-- 任务列表 -->
              <div class="task-list-container">
                <div class="task-list-header">
                  <span class="task-count">{{ currentTaskList.length }} 个任务</span>
                  <Badge
                    :count="getCurrentCategoryUncompletedCount()"
                    type="error"
                    class="uncompleted-count"
                  />
                </div>

                <div class="task-list-scroll">
                  <div
                    v-for="(task, index) in displayedTaskList"
                    :key="task.taskId"
                    class="task-list-item"
                    :class="{
                      'active': activeTask && activeTask.taskId === task.taskId,
                      'status-0': task.status === 0 || task.status === null,
                      'status-1': task.status === 1,
                      'status-2': task.status === 2,
                      'status-9': task.status === 9
                    }"
                    @click="selectTask(task)"
                  >
                    <div class="task-item-header">
                      <Icon
                        :type="getTaskIcon(task.taskType)"
                        :color="getTaskColor(task.status)"
                        size="16"
                      />
                      <span class="task-name">{{ task.taskName }}</span>
                      <Tag :color="getStatusColor(task.status)" size="small" class="status-tag status-tag--inline">
                        {{ getStatusText(task.status) }}
                      </Tag>
                    </div>
                    <div class="task-item-footer">
                      <span v-if="task.minLv" class="task-level">Lv.{{ task.minLv }}</span>
                    </div>
                  </div>

                  <!-- 空状态 -->
                  <div v-if="currentTaskList.length === 0" class="empty-task-list">
                    <Icon type="ios-list-box-outline" size="32" color="#ccc" />
                    <p>暂无任务</p>
                  </div>
                </div>

                <div v-if="(activeCategory === 'main' || activeCategory === 'branch') && currentTaskList.length > taskPageSize" class="task-list-pagination">
                  <Page
                    :current="taskPage"
                    :total="currentTaskList.length"
                    :page-size="taskPageSize"
                    simple
                    @on-change="handleTaskPageChange"
                  />
                </div>
              </div>
            </Card>
          </Col>

          <!-- 右侧任务详情 -->
          <Col span="16" class="task-right-column"  :class="{ 'mobile-show': isMobile && showDetail, 'mobile-hidden': isMobile && !showDetail }">
            <Card class="task-detail-card" :bordered="false">
              <!-- 移动端返回按钮（只在移动端显示） -->
              <div class="mobile-back-button"  @click="backToList()" v-if="isMobile">
                <Icon type="ios-arrow-back" />
                <span>返回列表</span>
              </div>

              <!-- 常规任务详情 -->
              <div v-if="taskCategory === 0 && taskObjectEject" class="task-detail-content">
                <!-- 任务标题区域 -->
                  <div class="task-detail-header">
                    <div class="task-title-section">
                      <h2 class="task-detail-title">
                        <Icon :type="getTaskIcon(taskObject.taskType)" :color="getTaskColor(taskObject.status)" />
                        {{ taskObject.taskName }}
                      </h2>
                      <div class="task-tags">
                        <Tag :color="getTaskTypeColor(taskObject.taskType)" size="small">
                          {{ getTaskTypeText(taskObject.taskType) }}
                        </Tag>
                        <Tag v-if="taskObject.minLv" color="blue" size="small">
                          <Icon type="ios-trending-up" />
                          Lv.{{ taskObject.minLv }}+
                        </Tag>
                        <Tag v-if="taskObject.status === 2" color="success" size="small">
                          <Icon type="ios-trophy" />
                          可领取
                        </Tag>
                      </div>
                    </div>
                    <!-- 移动端任务操作按钮优化 -->
                    <div class="task-actions-wrapper" :class="{ 'mobile': isMobile }">
                      <div class="task-actions">
                        <Button
                          v-if="taskObject.status === 0"
                          type="primary"
                          size="small"
                          @click="receiveTask()"
                          class="action-btn"
                        >
                          <Icon type="ios-play" />
                          <span class="btn-text">接取任务</span>
                        </Button>
                        <Button
                          v-if="taskObject.status === 2"
                          type="success"
                          size="small"
                          @click="claimRewards()"
                          class="action-btn"
                        >
                          <Icon type="ios-gift" />
                          <span class="btn-text">领取奖励</span>
                        </Button>
                        <Button
                          type="info"
                          ghost
                          size="small"
                          @click="refreshTask()"
                          title="刷新任务进度"
                          class="action-btn"
                        >
                          <Icon type="ios-refresh" />
                          <span class="btn-text">刷新</span>
                        </Button>
                      </div>
                    </div>
  <!--                  <div class="task-actions">
                      <Button
                        v-if="taskObject.status === 0"
                        type="primary"
                        size="small"
                        @click="receiveTask()"
                      >
                        <Icon type="ios-play" />
                        接取任务
                      </Button>
                      <Button
                        v-if="taskObject.status === 2"
                        type="success"
                        size="small"
                        @click="claimRewards()"
                      >
                        <Icon type="ios-gift" />
                        领取奖励
                      </Button>
                      <Button
                        type="info"
                        ghost
                        size="small"
                        @click="refreshTask()"
                        title="刷新任务进度"
                      >
                        <Icon type="ios-refresh" />
                        刷新
                      </Button>
                    </div>-->
                  </div>

                  <!-- 任务描述 -->
                  <div class="task-description">
                    <div class="section-title">
                      <Icon type="ios-book" />
                      <span>任务描述</span>
                    </div>
                    <div class="description-content" v-html="taskObject.taskDesci"></div>
                  </div>

                  <!-- 等级限制 -->
                  <div v-if="taskObject.minLv !== null" class="task-level-info">
                    <Icon type="ios-stats" />
                    <span>等级限制: {{ taskObject.minLv }} - {{ taskObject.maxLv }}</span>
                  </div>

                  <!-- 任务要求 -->
                  <div v-if="taskObject.needRule && taskObject.needRule.length > 0" class="task-requirements">
                    <div class="section-title">
                      <Icon type="ios-list-box" />
                      <span>任务要求</span>
                    </div>
                    <div class="requirements-list">
                      <div
                        v-for="(dtl, index) in taskObject.needRule"
                        :key="index"
                        class="requirement-item"
                        :class="{ 'completed': dtl.dtlSubNum >= dtl.dtlNeedNum }"
                      >
                        <div class="requirement-icon">
                          <Icon
                            :type="getRequirementIcon(dtl.dtlType)"
                            :color="dtl.dtlSubNum >= dtl.dtlNeedNum ? '#52c41a' : '#1890ff'"
                          />
                        </div>
                        <div class="requirement-content">
                          <div class="requirement-name">
                            {{ getRequirementText(dtl.dtlType) }} {{ dtl.dtlNeedName }}
                            <span v-if="dtl.dtlType === 'mat' && taskObject.status === 1 && dtl.dtlSubNum < dtl.dtlNeedNum"
                                  class="submit-btn"
                                  @click="submitTask(dtl.dtlNeedId, dtl.dtlId)">
                              <Icon type="md-add-circle" /> 提交
                            </span>
                          </div>
                          <div class="requirement-progress">
                            <span class="progress-text">
                              {{ dtl.dtlSubNum || 0 }} / {{ dtl.dtlNeedNum }}
                            </span>
                            <Progress
                              v-if="taskObject.status === 1"
                              :percent="Math.min(100, ((dtl.dtlSubNum || 0) / dtl.dtlNeedNum) * 100)"
                              size="small"
                              hide-info
                              style="width: 100px; margin-left: 8px;"
                            />
                          </div>
                        </div>
                      </div>
                    </div>

                    <!-- 提交材料弹窗 -->
                    <div v-if="subMatEject" class="material-submit-panel">
                      <div class="material-submit-content">
                        <div class="section-title">
                          <Icon type="ios-cube" />
                          <span>提交材料</span>
                        </div>
                        <div class="form-group">
                          <label>选择材料：</label>
                          <Select v-model="matPackId" style="width: 200px;" size="small">
                            <Option
                              v-for="item in materialSmallList"
                              :value="item.packItemId"
                              :key="item.packItemId"
                            >
                              {{ item.itemName }} (拥有: {{ item.itemNum }})
                            </Option>
                          </Select>
                        </div>
                        <div class="form-group">
                          <label>提交数量：</label>
                          <InputNumber
                            v-model="subTaskNum"
                            :min="1"
                            :max="getMaxSubmitNum()"
                            style="width: 120px;"
                            size="small"
                          />
                        </div>
                        <div class="submit-buttons">
                          <Button type="primary" size="small" @click="submitMatTask(currentDtlId)">
                            确认提交
                          </Button>
                          <Button size="small" style="margin-left: 8px;" @click="subMatEject = false">
                            取消
                          </Button>
                        </div>
                      </div>
                    </div>
                  </div>

                  <!-- 任务奖励 -->
                  <div v-if="taskObject.rewardRule && taskObject.rewardRule.length > 0" class="task-rewards">
                    <div class="section-title">
                      <Icon type="ios-star" />
                      <span>任务奖励</span>
                      <span v-if="taskObject.sectContr" class="sect-contribution">
                        宗门贡献: +{{ taskObject.sectContr }}
                      </span>
                    </div>
                    <div class="rewards-list">
                      <!-- 指定奖励（单选） -->
                      <div v-if="taskObject.appoint == 1" class="appointed-rewards">
                        <div class="reward-tip">请选择一个奖励：</div>
                        <RadioGroup v-model="rewardId"  class="reward-radio-group">
                          <Radio
                            v-for="(dtl, index) in taskObject.rewardRule"
                            :key="dtl.itemId"
                            :label="dtl.itemId"
                            class="reward-radio-item"
                          >
                            <div class="reward-item">
                              <Icon type="ios-cube" :color="dtl.typeDec === '材料' ? '#722ed1' : '#1890ff'" />
                              <span class="reward-name">{{ dtl.itemName }}</span>
                              <Tag color="orange" size="small">×{{ dtl.dropNum }}</Tag>
                              <span class="reward-type">{{ dtl.typeDec }}</span>
                            </div>
                          </Radio>
                        </RadioGroup>
                      </div>
                      <!-- 固定奖励 -->
                      <div v-else class="fixed-rewards">
                        <div
                          v-for="(dtl, index) in taskObject.rewardRule"
                          :key="dtl.itemId"
                          class="reward-item fixed"
                        >
                          <Icon type="ios-cube" :color="dtl.typeDec === '材料' ? '#722ed1' : '#1890ff'" />
                          <div class="reward-info">
                            <div class="reward-name">{{ dtl.itemName }}</div>
                            <div class="reward-details">
                              <Tag color="orange" size="small">×{{ dtl.dropNum }}</Tag>
                              <span class="reward-type">{{ dtl.typeDec }}</span>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

              <!-- 空状态 -->
              <div v-else class="empty-detail">
                <div class="empty-content">
                  <Icon type="ios-search" size="48" color="#ccc" />
                  <h3>选择任务</h3>
                  <p>请在左侧选择任务查看详情</p>
                </div>
              </div>
            </Card>
          </Col>
        </Row>
      </div>

      <!-- 悬赏任务内容 - PC端保持Row/Col布局 -->
      <div v-if="showRewardTab && activeTab === 'reward'" class="tab-pane reward-task-pane">
        <!-- 移动端悬赏分类按钮（只在移动端显示） -->
        <div class="mobile-reward-category-buttons">
          <Button
            :type="rewardActiveTab === 'myReward' ? 'primary' : 'default'"
            class="mobile-reward-category-btn"
            @click="swithTask('myReward')"
            size="small"
          >
            <Icon type="ios-list" />
            我的悬赏
          </Button>
          <Button
            :type="rewardActiveTab === 'publish' ? 'primary' : 'default'"
            class="mobile-reward-category-btn"
            @click="swithTask('publish')"
            size="small"
            style="color: #F43e06; border-color: #F43e06;"
          >
            <Icon type="ios-add-circle" />
            发布悬赏
          </Button>
          <Button
            :type="rewardActiveTab === 'list' ? 'primary' : 'default'"
            class="mobile-reward-category-btn"
            @click="openRewardList"
            size="small"
            ghost
            type="info"
          >
            <Icon type="ios-list" />
            悬赏列表
          </Button>
        </div>

        <Row :gutter="16" class="reward-layout-row">
          <!-- 左侧悬赏任务菜单 -->
          <Col span="8" class="reward-left-column"  :class="{
        'mobile-hidden': isMobile && (showRewardDetail || rewardActiveTab !== 'myReward')
      }">
            <Card class="reward-sidebar-card" :bordered="false">
              <!-- PC端悬赏分类按钮 -->
              <div class="reward-category-buttons pc-category">
                <Button
                  :type="rewardActiveTab === 'myReward' ? 'primary' : 'default'"
                  class="reward-category-btn"
                  @click="swithTask('myReward')"
                  size="small"
                  long
                >
                  <Icon type="ios-list" />
                  我的悬赏
                </Button>
                <Button
                  :type="rewardActiveTab === 'publish' ? 'primary' : 'default'"
                  class="reward-category-btn"
                  @click="swithTask('publish')"
                  size="small"
                  long
                  style="color: #F43e06; border-color: #F43e06;"
                >
                  <Icon type="ios-add-circle" />
                  发布悬赏
                </Button>
                <Button
                  :type="rewardActiveTab === 'list' ? 'primary' : 'default'"
                  class="reward-category-btn"
                  @click="openRewardList"
                  size="small"
                  long
                  ghost
                  type="info"
                >
                  <Icon type="ios-list" />
                  悬赏列表
                </Button>
              </div>
              <div v-if="rewardActiveTab === 'publish'" class="reward-list-container">
                <div class="reward-rules-detail">
                  <h4>悬赏规则：</h4>
                  <p>• 每人每天仅可发布两个悬赏任务</p>
                  <p>• 悬赏发布后不可修改</p>
                  <p>• 付出物品将从背包立即扣除</p>
                  <p>• 悬赏完成后收取25%手续费</p>
                </div>
              </div>
              <!-- 我的悬赏列表 -->
              <div v-if="rewardActiveTab === 'myReward'" class="reward-list-container">
                <div class="reward-list-header">
                  <span class="reward-count">{{ myJbOrTaskList.length }} 个悬赏</span>
                </div>

                <div class="reward-list-scroll">
                  <!-- 进行中的悬赏 -->
                  <div v-if="getActiveRewardTasks().length > 0" class="reward-section">
                    <div class="section-title-small">进行中</div>
                    <div
                      v-for="item in getActiveRewardTasks()"
                      :key="item.orTaskId"
                      class="reward-list-item"
                      :class="{ 'active': taskOrObject && taskOrObject.orTaskId === item.orTaskId }"
                      @click="selectRewardTask(item)"
                    >
                      <div class="reward-item-header">
                        <Icon type="ios-time" :color="getRewardStatusColor(item.busStatus)" />
                        <span class="reward-name">{{ item.name }}</span>
                        <Tag :color="getRewardStatusColor(item.busStatus)" size="small" class="status-tag status-tag--inline">
                          {{ getRewardStatusText(item.busStatus) }}
                        </Tag>
                      </div>
                      <div class="reward-item-footer"></div>
                    </div>
                  </div>

                  <!-- 已完成的悬赏 -->
                  <div v-if="getCompletedRewardTasks().length > 0" class="reward-section">
                    <div class="section-title-small">已完成</div>
                    <div
                      v-for="item in getCompletedRewardTasks()"
                      :key="item.orTaskId"
                      class="reward-list-item completed"
                      :class="{ 'active': taskOrObject && taskOrObject.orTaskId === item.orTaskId }"
                      @click="selectRewardTask(item)"
                    >
                      <div class="reward-item-header">
                        <Icon type="ios-checkmark-circle" color="#52c41a" />
                        <span class="reward-name">{{ item.name }}</span>
                        <Tag color="success" size="small" class="status-tag status-tag--inline">已完成</Tag>
                      </div>
                      <div class="reward-item-footer"></div>
                    </div>
                  </div>

                  <!-- 空状态 -->
                  <div v-if="myJbOrTaskList.length === 0" class="empty-reward-list">
                    <Icon type="ios-clipboard-outline" size="32" color="#ccc" />
                    <p>暂无悬赏任务</p>
                  </div>
                </div>
              </div>
            </Card>
          </Col>

          <!-- 右侧悬赏任务详情 -->
          <Col span="16" class="reward-right-column" :class="{
        'mobile-show': isMobile && (showRewardDetail || rewardActiveTab !== 'myReward'),
        'mobile-hidden': isMobile && !showRewardDetail && rewardActiveTab === 'myReward'
      }">
            <Card class="reward-detail-card" :bordered="false">
              <!-- 移动端返回按钮（只在移动端显示） -->
              <div class="mobile-back-button" @click="backToRewardList()"   v-if="isMobile && rewardActiveTab === 'myReward' && showRewardDetail" >
                <Icon type="ios-arrow-back" />
                <span>返回列表</span>
              </div>

              <!-- 悬赏任务详情 -->
              <div v-if="taskCategory === 2 && taskOrObject" class="reward-detail-content">
                <!-- 保持原有的悬赏任务详情内容不变 -->
                <div class="task-header">
                  <h2 class="task-title">{{ taskOrObject.name }}</h2>
                  <Tag :color="getRewardStatusColor(taskOrObject.busStatus)" class="status-tag">
                    {{ getRewardStatusText(taskOrObject.busStatus) }}
                  </Tag>
                </div>

                <div class="task-content">
                  <!-- 完成状态图片 -->
                  <div v-if="taskOrObject.busStatus === 3 || taskOrObject.busStatus === 4" class="status-image">
                    <img src="@/assets/success.png" alt="任务完成" />
                  </div>

                  <div class="task-description-section">
                    <p class="task-description" v-html="taskOrObject.taskDesc"></p>
                  </div>

                  <!-- 悬赏信息 -->
                  <div class="reward-info">
                    <h3 class="section-title">
                      <Icon type="ios-gift" />
                      悬赏信息
                    </h3>
                    <div class="info-grid">
                      <div class="info-item">
                        <Icon type="ios-cube" color="#2f90b9" />
                        <span class="info-label">悬赏物品:</span>
                        <span class="info-value">{{ taskOrObject.goodName }}</span>
                      </div>
                      <div class="info-item">
                        <Icon type="ios-cube" color="#2f90b9" />
                        <span class="info-label">悬赏数量:</span>
                        <span class="info-value">{{ taskOrObject.goodNum }}</span>
                      </div>
                      <div class="info-item">
                        <Icon type="ios-swap" color="#801DAE" />
                        <span class="info-label">付出物品:</span>
                        <span class="info-value">{{ taskOrObject.interchangeName }}</span>
                      </div>
                      <div class="info-item">
                        <Icon type="ios-swap" color="#801DAE" />
                        <span class="info-label">付出数量:</span>
                        <span class="info-value">{{ taskOrObject.interchangeNum }}</span>
                      </div>
                    </div>
                  </div>

                  <!-- 任务详情 -->
                  <div class="task-meta">
                    <h3 class="section-title">
                      <Icon type="ios-information-circle" />
                      任务详情
                    </h3>
                    <div class="info-grid">
                      <div class="info-item">
                        <Icon type="ios-person" color="#1890ff" />
                        <span class="info-label">悬赏人:</span>
                        <span class="info-value">{{ taskOrObject.sellerName }}</span>
                      </div>
                      <div class="info-item">
                        <Icon type="ios-calendar" color="#52c41a" />
                        <span class="info-label">期限:</span>
                        <span class="info-value">{{ taskOrObject.dayNum }} 天</span>
                      </div>
                      <div class="info-item">
                        <Icon type="ios-cash" color="#f5222d" />
                        <span class="info-label">押金:</span>
                        <span class="info-value">{{ taskOrObject.price }} 灵石</span>
                      </div>
                      <div class="info-item" v-if="taskOrObject.buyTime">
                        <Icon type="ios-time" color="#722ed1" />
                        <span class="info-label">揭榜时间:</span>
                        <span class="info-value">{{ taskOrObject.buyTime }}</span>
                      </div>
                    </div>
                  </div>

                  <!-- 任务操作 -->
                  <div class="task-actions">
                    <Button
                      v-if="taskOrObject.busStatus === 2"
                      type="error"
                      icon="md-checkmark"
                      @click="claimRewards2()"
                      class="action-button"
                      size="small"
                    >
                      交付悬赏物品
                    </Button>

                    <Button
                      v-if="taskOrObject.busStatus === 1"
                      type="warning"
                      icon="md-hand"
                      @click="jiebang(taskOrObject.orTaskId, 0)"
                      class="action-button"
                      size="small"
                    >
                      揭榜任务
                    </Button>

                    <Button
                      v-if="taskOrObject.busStatus === 3"
                      type="success"
                      icon="md-download"
                      @click="claimRewards3(taskOrObject.orTaskId)"
                      class="action-button"
                      size="small"
                    >
                      领取奖励
                    </Button>

                    <Button
                      v-if="taskOrObject.busStatus === 5"
                      type="default"
                      icon="md-undo"
                      @click="claimRewards3(taskOrObject.orTaskId)"
                      class="action-button"
                      size="small"
                    >
                      退还材料
                    </Button>
                  </div>
                </div>
              </div>

              <!-- 发布悬赏表单详情页 -->
              <div v-if="rewardActiveTab === 'publish'" class="publish-reward-detail">
                <div class="form-detail-header">
                  <h3 class="form-title">
                    <Icon type="ios-add-circle" />
                    发布悬赏任务
                  </h3>
                  <p class="form-desc">详细填写悬赏信息</p>
                </div>
                <!-- 这里可以显示更详细的信息 -->
                <!-- 发布悬赏表单 -->
                <div v-if="rewardActiveTab === 'publish'" class="publish-reward-sidebar">
                  <!-- 发布悬赏表单 - 简化版本 -->
                  <Form
                    ref="formValidate"
                    :model="formValidate"
                    :rules="ruleValidate"
                    :label-width="0"
                    class="simple-reward-form"
                  >
                    <span class="item-type2">悬赏任务名称：</span>
                    <FormItem prop="name">
                      <Input v-model="formValidate.name" placeholder="换稀有物品" size="small"></Input>
                    </FormItem>
                    <span class="item-type2">任务描述：</span>
                    <FormItem prop="desc">
                      <Input
                        v-model="formValidate.desc"
                        type="textarea"
                        :autosize="{minRows: 2, maxRows: 4}"
                        placeholder="走过路过别错过..."
                        size="small"
                      ></Input>
                    </FormItem>
                    <span class="item-type2">悬赏物品：</span>
                    <FormItem prop="rewardId">
                      <Select v-model="formValidate.rewardId" placeholder="缺啥" size="small">
                        <Option
                          v-for="item in materialExchangeList"
                          :value="item.itemId"
                          :key="item.itemId"
                        >
                          {{ item.itemName }}
                          <span class="item-type">『{{ item.typeDec }}』</span>
                        </Option>
                      </Select>
                    </FormItem>
                    <span class="item-type2">悬赏数量：</span>
                    <FormItem prop="rewardNum">
                      <InputNumber
                        v-model="formValidate.rewardNum"
                        placeholder="要几个"
                        :min="1"
                        style="width: 100%"
                        size="small"
                      ></InputNumber>
                    </FormItem>
                    <span class="item-type2">付出物品：</span>
                    <FormItem prop="exchangeId">
                      <Select v-model="formValidate.exchangeId" placeholder="给啥" size="small">
                        <Option
                          v-for="item in smallExchangeList"
                          :value="item.packItemId"
                          :key="item.packItemId"
                        >
                          <div class="exchange-option">
                            <span class="item-name">{{ item.itemName }} <span class="item-count">* {{ item.itemNum }}</span></span>
                            <span class="item-type">『{{ item.typeDec }}』</span>
                          </div>
                        </Option>
                      </Select>
                    </FormItem>
                    <span class="item-type2">付出数量：</span>
                    <FormItem prop="exchangeNum">
                      <InputNumber
                        v-model="formValidate.exchangeNum"
                        placeholder="给几个"
                        :min="1"
                        style="width: 100%"
                        size="small"
                      ></InputNumber>
                    </FormItem>
                    <span class="item-type2">悬赏天数：</span>
                    <FormItem prop="exchangeDay">
                      <Select v-model="formValidate.exchangeDay" placeholder="花多少时间" size="small">
                        <Option value="1">今日</Option>
                        <Option value="2">两日</Option>
                        <Option value="3">三日</Option>
                      </Select>
                    </FormItem>

                    <FormItem>
                      <Button
                        type="error"
                        @click="handleSubmit('formValidate')"
                        icon="md-send"
                        size="small"
                        long
                      >
                        发布悬赏
                      </Button>
                    </FormItem>
                  </Form>
                </div>
              </div>

              <!-- 悬赏列表详情 -->
              <div v-if="rewardActiveTab === 'list'" class="reward-list-detail">
                <div class="list-detail-header">
                  <h3 class="form-title">
                    <Icon type="ios-list" />
                    悬赏任务市场
                  </h3>
                  <p class="form-desc">浏览和接取其他玩家发布的悬赏</p>
                  <div class="search-bar">
                    <Input
                      v-model="orTaskName"
                      placeholder="请输入悬赏任务名称..."
                      style="width: 300px; margin-right: 10px;"
                      clearable
                      size="small"
                    />
                    <Button type="primary" @click="rewardList(1)" size="small">查询</Button>
                  </div>

                  <Table
                    border
                    :columns="columnsTuzing"
                    :data="orTaskList"
                    size="small"
                    class="compact-table"
                  >
                    <template #name="{ row }">
                      <strong>{{ row.name }}</strong>
                    </template>
                    <template #action="{ row, index }">
                      <Button
                        type="primary"
                        size="small"
                        v-if="row.busStatus === 1"
                        @click="jiebang(row.orTaskId, index)"
                      >
                        揭榜
                      </Button>
                    </template>
                  </Table>

                  <div style="margin-top: 16px; text-align: center;">
                    <Page
                      :total="total"
                      :page-size="pageSize"
                      @on-change="changepage"
                      size="small"
                      show-elevator
                      show-sizer
                    />
                  </div>
                </div>
              </div>
              <!-- 我的悬赏列表详情 -->
              <div v-if="rewardActiveTab === 'myReward'" class="reward-list-detail">
                <div class="list-detail-header">
                  <h3 class="form-title">
                    <Icon type="ios-list" />
                    我的悬赏任务
                  </h3>
                  <p class="form-desc">浏览自己发布的悬赏</p>
                  <Table
                    border
                    :columns="columnsTuzing"
                    :data="myOrTaskList"
                    size="small"
                    class="compact-table"
                  >
                    <template #name="{ row }">
                      <strong>{{ row.name }}</strong>
                    </template>
                    <template #action="{ row, index }">
                      <Button
                        type="primary"
                        size="small"
                        v-if="row.busStatus === 3"
                        @click="claimRewards3(row.orTaskId)"
                      >
                        领取奖励
                      </Button>
                      <Button
                        type="error"
                        size="small"
                        v-if="row.busStatus === 5"
                        @click="claimRewards3(row.orTaskId)"
                      >
                        退还材料
                      </Button>
                    </template>
                  </Table>
                </div>
              </div>
              <!-- 空状态 -->
              <div v-else-if="!taskOrObject " class="empty-reward-detail">
                <div class="empty-content">
                  <Icon type="ios-clipboard-outline" size="48" color="#ccc" />
                  <h3>悬赏任务详情</h3>
                  <p>请在左侧选择悬赏任务查看详情</p>
                </div>
              </div>
            </Card>
          </Col>
        </Row>
      </div>
    </div>

    <!-- 弹窗保持原样 -->
    <!-- 揭榜确认弹窗 -->
    <Modal v-model="jiebangEject" title="揭榜确认" @on-ok="show4()" width="360">
      <p>确定揭榜这个悬赏吗？</p>
    </Modal>
  </div>
</template>

<script>
export default {
  name: "TaskSystemModal",
  props: {
    visible: {
      type: Boolean,
      default: false
    },
    mode: {
      type: String,
      default: "all",
      validator(value) {
        return ["all", "normal", "reward"].includes(value);
      }
    }
  },
  data() {
    return {
      // 新增移动端检测
      isMobile: false,
      // 缺少这些控制变量
      showDetail: false, // 控制常规任务详情显示
      showRewardDetail: false, // 控制悬赏任务详情显示
      // 新增数据
      activeCategory: 'main', // 当前活动分类
      rewardActiveTab: 'myReward', // 悬赏任务的标签页
      currentTaskList: [], // 当前显示的任务列表
      taskPage: 1,
      taskPageSize: 10,
      activeTask: null, // 当前选中的任务

      // 其他原有数据保持不变...
      activeTab: 'normal', // 默认选中常规任务
      showTaskStatistics: false,
      // ... 其他原有数据
      openNames: ['1', '2', '3', '4'],
      theme: 'light',
      theme2: 'light',
      // 新增提交材料相关数据
      currentDtlId: null, // 当前要提交的任务详情ID
      currentNeedId: null, // 当前需要的材料ID
      // 表单数据
      formValidate: {
        charaId: '',
        name: '',
        rewardId: '',
        rewardNum: 1,
        interest: [],
        exchangeId: '',
        exchangeNum: 1,
        exchangeDay: '1',
        desc: ''
      },

      // 表单验证规则
      ruleValidate: {
        name: [
          { required: true, message: '悬赏任务名称必填', trigger: 'blur' }
        ],
        rewardId: [
          { required: true, message: '请选择悬赏物品', trigger: 'change' }
        ],
        rewardNum: [
          {
            required: true,
            message: '请输入悬赏数量',
            trigger: 'blur',
            validator: (rule, value, callback) => {
              if (value === null || value === undefined || value === '') {
                callback(new Error('请输入悬赏数量'));
              } else if (Number(value) <= 0) {
                callback(new Error('悬赏数量必须大于0'));
              } else {
                callback();
              }
            }
          }
        ],
        exchangeId: [
          { required: true, message: '请选择付出物品', trigger: 'change' }
        ],
        exchangeNum: [
          {
            required: true,
            message: '请输入付出数量',
            trigger: 'blur',
            validator: (rule, value, callback) => {
              if (value === null || value === undefined || value === '') {
                callback(new Error('请输入付出数量'));
              } else if (Number(value) <= 0) {
                callback(new Error('付出数量必须大于0'));
              } else {
                callback();
              }
            }
          }
        ],
        desc: [
          { required: true, message: '请输入任务描述', trigger: 'blur' },
          { type: 'string', min: 5, message: '描述至少5个字', trigger: 'blur' }
        ]
      },
      // 表格列配置
      columnsTuzing: [
        {
          title: '悬赏',
          key: 'name',
          width: 120
        },
        {
          title: '悬赏描述',
          key: 'taskDesc',
          width: 150
        },
        {
          title: '悬赏人',
          key: 'sellerName',
          width: 80
        },
        {
          title: '悬赏物品',
          key: 'goodName',
          width: 120,
          render: (h, params) => {
            let displayText = params.row.goodName;
            if (params.row.goodNum != null) {
              displayText = displayText + "*" + params.row.goodNum;
            }
            return h('span', displayText);
          }
        },
        {
          title: '交换物品',
          key: 'interchangeName',
          width: 120,
          render: (h, params) => {
            let displayText = params.row.interchangeName;
            if (params.row.interchangeNum != null) {
              displayText = displayText + "*" + params.row.interchangeNum;
            }
            return h('span', displayText);
          }
        },
        {
          title: '赏金',
          key: 'price',
          width: 80
        },
        {
          title: '悬赏状态',
          key: 'busStatus',
          width: 90,
          render: (h, params) => {
            switch (params.row.busStatus) {
              case 1:
                return h('Tag', { props: { color: 'blue' } }, '未接取');
              case 2:
                return h('Tag', { props: { color: 'orange' } }, '悬赏中');
              case 3:
                return h('Tag', { props: { color: 'green' } }, '待领取');
              case 4:
                return h('Tag', { props: { color: 'success' } }, '已完成');
              case 5:
                return h('Tag', { props: { color: 'default' } }, '已过期');
              default:
                return h('span', '未知');
            }
          },
        },
        {
          title: '操作',
          slot: 'action',
          width: 80,
          align: 'center'
        }
      ],

      // 状态数据
      tradingBankEject: false,
      orTaskName: '',
      pageSize: 10,
      total: 0,
      orTaskList: [],
      myOrTaskList: [],
      myJbOrTaskList: [],
      playersList: [],
      subTaskEject: false,
      subMatEject: false,
      subTaskNum: 0,
      matPackId: "",
      materialList: [],
      materialExchangeList: [],
      materialSmallList: [],
      smallExchangeList: [],
      playersList1: [],
      playersList2: [],
      playersList3: [],
      playersList4: [],
      taskObjectEject: false,
      jiebangEject: false,
      jbTaskId: "",
      jbIndex: 0,
      taskObject: {},
      taskOrObject: {},
      taskCategory: 0,
      rewardId: 0,
    };
  },
  computed: {
    showNormalTab() {
      return this.mode === "all" || this.mode === "normal";
    },
    showRewardTab() {
      return this.mode === "all" || this.mode === "reward";
    },
    showTopTabs() {
      return this.mode === "all";
    },
    // 计算未完成任务数量
    getCurrentCategoryUncompletedCount() {
      return () => {
        return this.currentTaskList.filter(task => !this.isTaskCompleted(task)).length;
      };
    },
    // 其他计算属性保持不变...
    totalTaskCount() {
      if (this.mode === "normal") return this.playersList.length;
      if (this.mode === "reward") return this.myJbOrTaskList.length;
      return this.playersList.length + this.myJbOrTaskList.length;
    },

    completedTaskCount() {
      const normalCompleted = this.playersList.filter(task => this.isTaskCompleted(task)).length;
      const rewardCompleted = this.getCompletedRewardTasks().length;
      if (this.mode === "normal") return normalCompleted;
      if (this.mode === "reward") return rewardCompleted;
      return normalCompleted + rewardCompleted;
    },

    uncompletedTaskCount() {
      const normalUncompleted = this.playersList.filter(task => !this.isTaskCompleted(task)).length;
      const rewardUncompleted = this.getActiveRewardTasks().length;
      if (this.mode === "normal") return normalUncompleted;
      if (this.mode === "reward") return rewardUncompleted;
      return normalUncompleted + rewardUncompleted;
    },

    displayedTaskList() {
      if (this.activeCategory !== 'main' && this.activeCategory !== 'branch') {
        return this.currentTaskList;
      }
      const start = (this.taskPage - 1) * this.taskPageSize;
      return this.currentTaskList.slice(start, start + this.taskPageSize);
    }
  },
  watch: {
    mode: {
      handler() {
        this.ensureActiveTabByMode();
      },
      immediate: true
    },
    // 监听分类切换
    activeCategory(newVal) {
      this.filterTasks(newVal);
    },
    // 监听任务列表变化
    playersList1: {
      handler() {
        this.filterTasks(this.activeCategory);
      },
      immediate: true
    },
    playersList2: {
      handler() {
        this.filterTasks(this.activeCategory);
      },
      immediate: true
    },
    playersList3: {
      handler() {
        this.filterTasks(this.activeCategory);
      },
      immediate: true
    },
    playersList4: {
      handler() {
        this.filterTasks(this.activeCategory);
      },
      immediate: true
    },
    // 其他监听器保持不变...
    activeTab(newTab) {
      if (!this.isTabAllowed(newTab)) {
        this.ensureActiveTabByMode();
        return;
      }
      // Tab切换时重置选中状态
      if (newTab === 'normal') {
        this.taskCategory = 0;
      } else if (newTab === 'reward' && this.showRewardTab) {
        this.taskCategory = 0; // 重置为悬赏任务列表状态
        this.refreshAllTasks();
        //this.selectTaskCate(0);
      }
    },

    visible(newVal) {
      if (newVal) {
        // 弹窗打开时自动刷新数据
        this.refreshAllTasks();
      }
    }
  },
  mounted() {
    // 检测移动端
    this.checkMobile();
    window.addEventListener('resize', this.checkMobile);
  },
  beforeDestroy() {
    window.removeEventListener('resize', this.checkMobile);
  },
  methods: {
    isTabAllowed(tab) {
      if (tab === "normal") return this.showNormalTab;
      if (tab === "reward") return this.showRewardTab;
      return false;
    },
    ensureActiveTabByMode() {
      if (this.mode === "normal") {
        this.activeTab = "normal";
        this.taskCategory = 0;
        return;
      }
      if (this.mode === "reward") {
        this.activeTab = "reward";
        return;
      }
      if (!this.isTabAllowed(this.activeTab)) {
        this.activeTab = "normal";
      }
    },
    // 检查是否为移动端
    checkMobile() {
      const responsive = this.$store && this.$store.state && this.$store.state.responsive
        ? this.$store.state.responsive
        : null;
      this.isMobile = responsive ? !!responsive.isMobile : window.innerWidth <= 768;
    },
    // 返回列表（常规任务）- 需要修改返回按钮的点击事件
    backToList() {
      if (this.isMobile) {
        this.showDetail = false;
        // 可以延迟一点再隐藏内容，让动画完成
        setTimeout(() => {
          this.taskObjectEject = false;
        }, 300);
      }
    },

    // 返回列表（悬赏任务）- 需要修改返回按钮的点击事件
    backToRewardList() {
      if (this.isMobile) {
        this.showRewardDetail = false;
        // 延迟一点再重置状态，让动画完成
        setTimeout(() => {
          this.taskCategory = 0;
          this.taskOrObject = null;
        }, 300);
      } else {
        this.taskCategory = 0;
        this.taskOrObject = null;
      }
    },
    swithTask(category){
      this. rewardActiveTab = category;
      this.taskCategory = 0;
      // 移动端：如果是我的悬赏，重置详情显示状态
      if (this.isMobile && category === 'myReward') {
        this.showRewardDetail = false;
        this.taskCategory = 0; // 确保显示列表
      }
    },
    // 切换任务分类
    switchCategory(category) {
      this.activeCategory = category;
      this.taskPage = 1;
      this.activeTask = null;
      this.taskObjectEject = false;
    },

    // 过滤任务列表
    sortTasksByStatus(taskList) {
      const statusOrder = {
        1: 0, // 进行中
        0: 1, // 未接取
        2: 2, // 待领取
        3: 2, // 已完成
        9: 2  // 已失败
      };
      return (taskList || [])
        .map((task, index) => ({ task, index }))
        .sort((left, right) => {
          const leftOrder = statusOrder[Number(left.task.status)] === undefined ? 2 : statusOrder[Number(left.task.status)];
          const rightOrder = statusOrder[Number(right.task.status)] === undefined ? 2 : statusOrder[Number(right.task.status)];
          return leftOrder - rightOrder || left.index - right.index;
        })
        .map(entry => entry.task);
    },

    filterTasks(category) {
      switch(category) {
        case 'main':
          this.currentTaskList = this.sortTasksByStatus(this.playersList1);
          break;
        case 'branch':
          this.currentTaskList = this.sortTasksByStatus(this.playersList4);
          break;
        case 'daily':
          this.currentTaskList = this.sortTasksByStatus(this.playersList2);
          break;
        case 'sect':
          this.currentTaskList = this.sortTasksByStatus(this.playersList3);
          break;
        case 'completed':
          // 合并所有已完成的任务
          const allCompleted = [
            ...this.playersList1.filter(task => this.isTaskCompleted(task)),
            ...this.playersList2.filter(task => this.isTaskCompleted(task)),
            ...this.playersList3.filter(task => this.isTaskCompleted(task)),
            ...this.playersList4.filter(task => this.isTaskCompleted(task))
          ];
          this.currentTaskList = this.sortTasksByStatus(allCompleted);
          break;
        default:
          this.currentTaskList = [];
      }
      this.normalizeTaskPage();
    },

    handleTaskPageChange(page) {
      this.taskPage = page;
      this.activeTask = null;
      this.taskObjectEject = false;
      if (this.isMobile) {
        this.showDetail = false;
      }
    },

    normalizeTaskPage() {
      const maxPage = Math.max(1, Math.ceil(this.currentTaskList.length / this.taskPageSize));
      if (this.taskPage > maxPage) {
        this.taskPage = maxPage;
      }
    },

    // 选择任务
    selectTask(task) {
      this.activeTask = task;
      this.taskObject = task;
      this.taskObjectEject = true;
      this.taskCategory = 0;

      // 添加移动端控制
      if (this.isMobile) {
        this.showDetail = true;
      }
    },
    // 选择悬赏任务
    selectRewardTask(task) {
      this.taskOrObject = task;
      this.taskCategory = 2;

      // 添加移动端控制
      if (this.isMobile) {
        this.showRewardDetail = true;
      }
    },

    // 获取任务图标
    getTaskIcon(taskType) {
      switch(taskType) {
        case 'mainTask': return 'md-alert';
        case 'guideTask': return 'ios-alert';
        case 'actTask': return 'ios-ionic-outline';
        case 'sectTask': return 'ios-map-outline';
        default: return 'ios-list';
      }
    },

    // 获取任务颜色
    getTaskColor(status) {
      switch(status) {
        case 0: case null: return '#666';
        case 1: return '#1890ff';
        case 2: return '#52c41a';
        case 9: return '#ff4d4f';
        default: return '#666';
      }
    },

    // 获取任务类型文本
    getTaskTypeText(taskType) {
      switch(taskType) {
        case 'mainTask': return '主线';
        case 'guideTask': return '支线';
        case 'actTask': return '活动';
        case 'sectTask': return '宗门';
        default: return '任务';
      }
    },

    // 获取任务类型颜色
    getTaskTypeColor(taskType) {
      switch(taskType) {
        case 'mainTask': return 'red';
        case 'guideTask': return 'orange';
        case 'actTask': return 'blue';
        case 'sectTask': return 'green';
        default: return 'default';
      }
    },

    // 获取状态文本
    getStatusText(status) {
      switch(status) {
        case 0: case null: return '未接取';
        case 1: return '进行中';
        case 2: return '待领取';
        case 3: return '已完成';
        case 9: return '已失败';
        default: return '';
      }
    },

    // 获取状态颜色
    getStatusColor(status) {
      switch(status) {
        case 0: case null: return 'default';
        case 1: return 'primary';
        case 2: return 'success';
        case 9: return 'error';
        default: return 'default';
      }
    },

    // 获取要求图标
    getRequirementIcon(dtlType) {
      switch(dtlType) {
        case 'map': return 'ios-navigate-outline';
        case 'mat': return 'ios-leaf-outline';
        case 'monster': return 'ios-bonfire-outline';
        case 'note': return 'ios-brush-outline';
        default: return 'ios-list';
      }
    },

    // 获取要求文本
    getRequirementText(dtlType) {
      switch(dtlType) {
        case 'map': return '探索';
        case 'mat': return '收集';
        case 'monster': return '消灭';
        case 'note': return '完成';
        default: return '';
      }
    },

    // 修改submitTask方法
    submitTask(dtlNeedId, dtlId) {
      this.currentDtlId = dtlId;
      this.currentNeedId = dtlNeedId;
      this.subMatEject = true;
      this.materialSmallList = [];

      for (let i = 0; i < this.materialList.length; i++) {
        if(this.materialList[i].itemId == dtlNeedId) {
          this.materialSmallList.push(this.materialList[i]);
        }
      }
    },


    // 打开悬赏列表
    openRewardList() {
      if (!this.showRewardTab) return;
      this.rewardActiveTab = 'list';
      this.rewardList(1);
      this.taskCategory = 0;
    },

    // 发布悬赏
    publishReward() {
      if (!this.showRewardTab) return;
      this.activeTab = 'reward';
      this.rewardActiveTab = 'publish';
      this.gainExchangeItemList();
    },

    // 其他原有方法保持不变...
    // 新增方法：刷新所有任务
    refreshAllTasks() {
      this.getPlaters();
      if (this.showRewardTab) {
        this.selectTaskCate(0);
      } else {
        this.taskCategory = 0;
      }
      //this.$Notice.success({ title: "所有任务列表已刷新" });
    },
    // 修改任务选择方法，切换到常规任务Tab
    handleTaskSelect(name) {
      if (this.showNormalTab) {
        this.activeTab = 'normal'; // 确保在常规任务Tab
      }
      const task = this.playersList.find(item => item.taskId == name);
      if (task) {
        this.taskObject = task;
        this.taskObjectEject = true;
        this.taskCategory = 0;
      }
    },

    // 修改悬赏任务选择方法，切换到悬赏任务Tab
    handleRewardSelect(name) {
      if (!this.showRewardTab) return;
      this.activeTab = 'reward'; // 确保在悬赏任务Tab
      const task = this.myJbOrTaskList.find(item => item.orTaskId == name);
      if (task) {
        this.taskOrObject = task;
        this.taskCategory = 2;
      }
    },
    // 修改发布悬赏方法，切换到悬赏任务Tab
    selectTaskCate(value) {
      if (!this.showRewardTab) return;
      this.activeTab = 'reward'; // 切换到悬赏任务Tab
      this.taskCategory = value;
      this.gainExchangeItemList();
    },

    // 修改悬赏列表方法，切换到悬赏任务Tab
    rewardList(index) {
      this.tradingBankEject = true;
      this.$http.post("gameTask/getOfferRewardList?page=" + index + "&pageSize=" + this.pageSize + '&name=' + this.orTaskName).then((res) => {
        this.orTaskList = res.data.rows;
        this.total = res.data.records;
      }).catch((err) => {
        this.$Notice.info({ title: '暂无悬赏任务可接取!' });
      });
    },
    // 判断任务是否完成
    isTaskCompleted(task) {
      return task.status === 2 || task.status === 3;
    },

    // 获取未完成任务
    getUncompletedTasks(taskList) {
      return taskList.filter(task => !this.isTaskCompleted(task));
    },

    // 获取已完成任务
    getCompletedTasks(taskList) {
      return taskList.filter(task => this.isTaskCompleted(task));
    },

    // 获取未完成任务数量
    getUncompletedCount(taskList) {
      return this.getUncompletedTasks(taskList).length;
    },

    // 悬赏任务分类方法
    getActiveRewardTasks() {
      return this.myJbOrTaskList.filter(task =>
        task.busStatus === 1 || task.busStatus === 2 || task.busStatus === 3
      );
    },

    getCompletedRewardTasks() {
      return this.myJbOrTaskList.filter(task => task.busStatus === 4);
    },

    getExpiredRewardTasks() {
      return this.myJbOrTaskList.filter(task => task.busStatus === 5);
    },

    getUncompletedRewardCount() {
      return this.getActiveRewardTasks().length;
    },

    // 获取任务状态颜色
    getTaskStatusColor(status) {
      const colorMap = {
        0: 'blue',    // 未接取
        1: 'orange',  // 进行中
        2: 'green',   // 待领取
        9: 'green'    // 已完成
      };
      return colorMap[status] || 'default';
    },

    // 获取任务状态文本
    getTaskStatusText(status) {
      const textMap = {
        0: '未接取',
        1: '进行中',
        2: '待领取',
        3: '已完成'
      };
      return textMap[status] || '未接取';
    },

    // 获取悬赏任务状态颜色
    getRewardStatusColor(status) {
      const colorMap = {
        1: 'blue',    // 未接取
        2: 'orange',  // 进行中
        3: 'green',   // 待领取
        4: 'success', // 已完成
        5: 'default'  // 已过期
      };
      return colorMap[status] || 'default';
    },

    // 获取悬赏任务状态文本
    getRewardStatusText(status) {
      const textMap = {
        1: '未接取',
        2: '进行中',
        3: '待领取',
        4: '已完成',
        5: '已过期'
      };
      return textMap[status] || '未知状态';
    },

    // 处理任务列表数据
    processTaskList(taskList) {
      this.playersList = taskList;
      this.playersList1 = [];
      this.playersList2 = [];
      this.playersList3 = [];
      this.playersList4 = [];

      taskList.forEach(item => {
        if (item.status == null) {
          item.status = 0;
        }

        // 设置状态文本
        switch (item.status) {
          case 0:
          case null:
            item.statusMsg = "未接取";
            break;
          case 1:
            item.statusMsg = "进行中";
            break;
          case 2:
            item.statusMsg = "待领取";
            break;
          case 9:
            item.statusMsg = "已完成";
            break;
          default:
            item.statusMsg = "";
        }

        // 分类任务
        switch (item.taskType) {
          case 'mainTask':
            this.playersList1.push(item);
            break;
          case 'actTask':
            this.playersList2.push(item);
            break;
          case 'sectTask':
            if (item.status !== 9) {
              this.playersList3.push(item);
            }
            break;
          case 'guideTask':
            this.playersList4.push(item);
            break;
        }
      });
    },

    // 其他原有方法保持不变
    getRequirementClass(type) {
      const classMap = {
        'map': 'map-requirement',
        'mat': 'material-requirement',
        'monster': 'monster-requirement',
        'note': 'note-requirement'
      };
      return classMap[type] || '';
    },
    // 原有的其他方法...
    getPlaters() {
      this.myRewardList(1);
      this.myRewardList(2);
      this.subMatEject = false;

      this.materialList = this.$store.state.materialList;

      this.$http.post("/gameTask/queryTaskList?charaId=" + this.getCookie("charaId"), {
        headers: {
          'Authorization': this.getCookie("token")
        }
      }).then((res) => {
        this.processTaskList(res.data.data);
      });
    },

    /*submitTask(dtlNeedId, dtlId) {
      this.subMatEject = true;
      this.materialSmallList = [];
      this.smallExchangeList = [];

      this.currentNeedId = dtlNeedId;
      this.currentDtlId = dtlId;
      this.subMatEject = true;
      this.matPackId = "";
      this.subTaskNum = 0;

      for (let i = 0; i < this.materialList.length; i++) {
        if (this.materialList[i].itemId == dtlNeedId) {
          this.materialSmallList.push(this.materialList[i]);
        }
        if (this.materialList[i].isExchange === 1) {
          this.smallExchangeList.push(this.materialList[i]);
        }
      }
    },*/

    refreshMaterialPackage() {
      this.$http
        .post("/gameChara/getCharaMaterial/?charaId=" + this.getCookie("charaId"))
        .then((res) => {
          this.materialList = res.data.data;
        })
        .catch((err) => {
          this.$Notice.warning({ title: "获取材料包裹失败,请联系管理员" });
        });
    },

    gainExchangeItemList() {
      this.$http
        .post("/gameMarket/gainExchangeItemList/?charaId=" + this.getCookie("charaId"))
        .then((res) => {
          //this.$Notice.success({ title: res.data.msg });
          this.materialExchangeList = res.data.data;
          this.materialList = this.$store.state.materialList;
          this.smallExchangeList = [];

          for (let i = 0; i < this.materialList.length; i++) {
            if (this.materialList[i].isExchange === 1) {
              this.smallExchangeList.push(this.materialList[i]);
            }
          }
        })
        .catch((err) => {
          this.$Notice.warning({ title: err.data.msg });
        });
    },
    // 获取最大可提交数量
    getMaxSubmitNum() {
      if (!this.matPackId) return 0;
      const material = this.materialSmallList.find(item => item.packItemId == this.matPackId);
      return material ? material.itemNum : 0;
    },
    submitMatTask(dtlId) {
      this.$http.post("/gameTask/submitMatTask?charaId=" +
        this.getCookie("charaId") +
        "&taskId=" + dtlId +
        "&packId=" + this.matPackId +
        "&packNum=" + this.subTaskNum
      ).then((res) => {
        this.$Notice.success({ title: res.data.msg });
        this.refreshTask();
        this.refreshMaterialPackage();
      }).catch((err) => {
        this.$Notice.warning({ title: err.data.msg });
      });
    },


    refreshTask() {
      this.$http.post("/gameTask/refreshTask?charaId=" +
        this.getCookie("charaId") +
        "&taskId=" + this.taskObject.taskId
      ).then((res) => {
        this.subMatEject = false;
        this.taskObject = res.data.data;
        this.taskObjectEject = true;
        this.taskCategory = 0;

        if (this.taskObject.status === null) {
          this.taskObject.status = 0;
        }

        this.materialList = this.$store.state.materialList;
        //this.$Notice.success({ title: "已刷新" });
      }).catch((err) => {
        this.$Notice.warning({ title: err.data.msg });
      });
    },

    // 其他悬赏相关方法
    jiebang(taskId, index) {
      this.jiebangEject = true;
      this.jbTaskId = taskId;
      this.jbIndex = index;
    },

    show4() {
      this.$http.post("/gameTask/playersUnveiling?charaId=" +
        this.getCookie("charaId") +
        "&orId=" + this.jbTaskId
      ).then((res) => {
        this.$Notice.success({ title: res.data.msg });
        this.orTaskList.splice(this.jbIndex, 1);
        this.myRewardList(2);
      }).catch((err) => {
        this.$Notice.warning({ title: err.data.msg });
      });
    },

    claimRewards2() {
      this.$http.post("/gameTask/playersDeliveryBounty?charaId=" +
        this.getCookie("charaId") +
        "&orId=" + this.taskOrObject.orTaskId
      ).then((res) => {
        this.taskOrObject.busStatus = 3;
        this.$Notice.success({ title: res.data.msg });
      }).catch((err) => {
        this.$Notice.warning({ title: err.data.msg });
      });
    },

    claimRewards3(taskId) {
      this.$http.post("/gameTask/playersRewardBounty?charaId=" +
        this.getCookie("charaId") +
        "&orId=" + taskId
      ).then((res) => {
        this.$Notice.success({ title: res.data.msg });
        this.myRewardList(1);
      }).catch((err) => {
        this.$Notice.warning({ title: err.data.msg });
      });
    },

    receiveTask() {
      this.$http.post("/gameTask/pickUpTask?charaId=" +
        this.getCookie("charaId") +
        "&taskId=" + this.taskObject.taskId
      ).then((res) => {
        this.$Notice.success({ title: res.data.msg });
        // 刷新常规任务列表
        this.refreshTask();
        this.getPlaters();

        // 如果是悬赏任务，刷新悬赏任务列表
        if (this.activeTab === 'reward' || this.taskObject.taskType === 'reward') {
          this.myRewardList(1); // 刷新我的悬赏列表
          this.myRewardList(2); // 刷新接取的悬赏列表
        }
      });
    },

    claimRewards() {
      if (this.taskObject.appoint === 1 && this.rewardId == 0) {
        this.$Notice.warning({ title: "必须指定奖励!" });
      } else {
        this.$http.post("/gameTask/claimRewards?charaId=" +
          this.getCookie("charaId") +
          "&taskId=" + this.taskObject.taskId +
          "&rewardId=" + this.rewardId
        ).then((res) => {
          this.$Notice.success({ title: res.data.msg });
          // 刷新任务状态
          this.refreshTask();
          this.getPlaters();

          // 刷新悬赏任务列表
          if (this.activeTab === 'reward') {
            this.myRewardList(1);
            this.myRewardList(2);
          }
        }).catch((err) => {
          this.$Notice.warning({ title: err.data.msg });
        });
      }
    },

    handleSubmit(name) {
      this.$refs[name].validate((valid) => {
        if (valid) {
          // 确保数值类型正确
          const formData = {
            ...this.formValidate,
            rewardNum: Number(this.formValidate.rewardNum),
            exchangeNum: Number(this.formValidate.exchangeNum),
            charaId: this.getCookie("charaId")
          };

          console.log('提交表单数据:', formData);

          this.$http.post("gameTask/offerReward", formData).then((res) => {
            this.$Notice.success({ title: res.data.msg });
            this.handleReset('formValidate');
            this.myRewardList(1);
            this.taskCategory = 0;
          }).catch((err) => {
            this.$Notice.error({ title: err.data.msg || '发布悬赏失败' });
          });
        } else {
          this.$Notice.error({
            title: '表单验证失败',
            desc: '请检查表单填写是否正确'
          });
        }
      });
    },

    changepage(index) {
      this.rewardList(index);
    },

    myRewardList(type) {
      this.$http.post("gameTask/getOfferRewardReceivedList?charaId=" +
        this.getCookie("charaId") +
        "&type=" + type
      ).then((res) => {
        if (type === 1) {
          this.myOrTaskList = res.data.data;
        } else {
          this.myJbOrTaskList = res.data.data;
        }
      }).catch((err) => {
        this.$Notice.info({ title: '暂无!' });
      });
    },

    handleReset(name) {
      this.$refs[name].resetFields();
      // 手动重置数值字段的默认值
      this.$nextTick(() => {
        this.formValidate.rewardNum = 1;
        this.formValidate.exchangeNum = 1;
      });
    },

    getCookie(name) {
      // Cookie获取逻辑
      const value = `; ${document.cookie}`;
      const parts = value.split(`; ${name}=`);
      if (parts.length === 2) return parts.pop().split(';').shift();
    }
  }
};
</script>

<style scoped>
/* 移动端特定样式 - 只在移动端生效 */
@media (max-width: 768px) {
  .task-system-modal {
    height: calc(100dvh - 122px);
    min-height: 0;
    display: flex;
    flex-direction: column;
    overflow: hidden;
  }

  .modal-header-actions,
  .top-tabs,
  .mobile-category-buttons {
    flex: 0 0 auto;
  }

  .task-container-modal {
    height: auto !important;
    min-height: 0 !important;
    flex: 1 1 auto;
    overflow: hidden !important;
  }

  .normal-task-pane {
    height: 100% !important;
    min-height: 0 !important;
    display: flex;
    flex-direction: column;
  }

  .task-list-container {
    min-height: 0 !important;
    height: 100% !important;
  }

  .task-list-scroll {
    flex: 1 1 auto !important;
    min-height: 0 !important;
    overflow-y: auto !important;
    padding: 8px !important;
    -webkit-overflow-scrolling: touch;
  }

  .task-list-pagination {
    flex: 0 0 52px !important;
    min-height: 52px !important;
    display: flex !important;
    align-items: center !important;
    justify-content: center !important;
    position: relative !important;
    bottom: auto !important;
    z-index: 8 !important;
    background: #fff !important;
    border-top: 1px solid #e8eaec !important;
  }

  .task-detail-card {
    padding-bottom: 76px !important;
  }

  .task-layout-row {
    height: auto !important;
    min-height: 0 !important;
    flex: 1 1 auto;
  }

  .task-left-column,
  .task-sidebar-card,
  .task-sidebar-card :deep(.ivu-card-body) {
    min-height: 0 !important;
  }

  .task-actions-wrapper.mobile {
    position: sticky !important;
    bottom: 0 !important;
    z-index: 9 !important;
    background: #fff !important;
    padding: 8px 0 !important;
  }

  .task-actions-wrapper.mobile .task-actions {
    margin: 0 !important;
  }

  .task-detail-content,
  .reward-detail-content {
    padding-bottom: 72px !important;
  }
  .task-actions-wrapper.mobile {
    width: 100%;
    margin-top: 8px;
  }
  .task-detail-content {
    height: calc(70vh) !important;
   /* overflow-y: auto !important;*/
  }
  .task-actions-wrapper.mobile .task-actions {
    width: 100%;
    justify-content: space-between;
  }

  .task-actions-wrapper.mobile .action-btn {
    flex: 1;
    min-width: 0;
    padding: 0 8px;
  }

  .task-actions-wrapper.mobile .btn-text {
    font-size: 12px;
  }

  /* 移动端标题区域优化 */
  .task-detail-header {
    flex-direction: column;
    align-items: stretch;
  }

  .task-detail-title {
    font-size: 16px;
  }

  .task-tags {
    gap: 4px;
  }
  /* 隐藏PC端分类按钮 */
  .pc-category {
    display: none !important;
  }
  /* 移动端列样式 */
  .task-left-column,
  .reward-left-column,
  .task-right-column,
  .reward-right-column {
    position: absolute !important;
    top: 0 !important;
    left: 0 !important;
    width: 100% !important;
    height: 100% !important;
    transition: transform 0.3s ease-in-out !important;
  }

  /* 左侧列（列表） */
  .task-left-column,
  .reward-left-column {
    transform: translateX(0) !important;
    z-index: 1 !important;
  }

  /* 右侧列（详情） */
  .task-right-column,
  .reward-right-column {
    transform: translateX(100%) !important;
    z-index: 2 !important;
  }

  /* 显示详情时 */
  .task-left-column.mobile-hidden,
  .reward-left-column.mobile-hidden {
    transform: translateX(-100%) !important;
  }

  .task-right-column.mobile-show,
  .reward-right-column.mobile-show {
    transform: translateX(0) !important;
  }
  /* 显示移动端分类按钮 */
  .mobile-category-buttons,
  .mobile-reward-category-buttons {
    display: block !important;
    margin-bottom: 12px;
  }

  /* 移动端分类按钮样式 */
  .mobile-category-buttons {
    padding: 8px 12px;
    background: white;
    border-bottom: 1px solid #e8e8e8;
  }

  .category-scroll {
    display: flex;
    gap: 8px;
    overflow-x: auto;
    white-space: nowrap;
    padding-bottom: 4px;
    scrollbar-width: none;
  }

  .category-scroll::-webkit-scrollbar {
    display: none;
  }

  .mobile-category-buttons .category-btn {
    flex-shrink: 0;
    min-width: 60px;
    height: 32px;
    padding: 0 12px;
    font-size: 12px;
  }

  /* 移动端悬赏分类按钮 */
  .mobile-reward-category-buttons {
    display: flex !important;
    gap: 8px;
    padding: 8px 12px;
    background: white;
    border-bottom: 1px solid #e8e8e8;
  }

  .mobile-reward-category-btn {
    flex: 1;
    height: 32px;
    font-size: 12px;
    padding: 0 8px;
  }

  /* 移动端布局调整 */
  .task-layout-row,
  .reward-layout-row {
    display: block !important;
    height: auto !important;
    min-height: 0 !important;
    flex: 1 1 auto !important;
    position: relative !important;
    overflow: hidden !important;
  }
  .reward-left-column,
  .reward-right-column {
    position: absolute !important;
    top: 0 !important;
    left: 0 !important;
    width: 100% !important;
    height: 100% !important;
    transition: transform 0.3s ease-in-out !important;
    background: white !important;
  }
  /* 左侧列（悬赏列表） */
  .reward-left-column {
    transform: translateX(0) !important;
    z-index: 1 !important;
  }

  /* 右侧列（悬赏详情） */
  .reward-right-column {
    transform: translateX(100%) !important;
    z-index: 2 !important;
  }
  /* 显示详情时 */
  .reward-left-column.mobile-hidden {
    transform: translateX(-100%) !important;
  }

  .reward-right-column.mobile-show {
    transform: translateX(0) !important;
  }

  /* 悬赏分类按钮 */
  .mobile-reward-category-buttons {
    display: flex !important;
    gap: 8px;
    padding: 8px 12px;
    background: white;
    border-bottom: 1px solid #e8e8e8;
    margin-bottom: 8px;
  }

  .mobile-reward-category-btn {
    flex: 1;
    height: 32px;
    font-size: 12px;
    padding: 0 8px;
  }

  /* 我的悬赏列表 */
  .reward-list-container {
    height: 100%;
    display: flex;
    flex-direction: column;
    overflow: hidden;
  }

  .reward-list-scroll {
    flex: 1;
    overflow-y: auto;
    padding: 8px;
  }

  /* 空状态 */
  .empty-reward-list {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding: 40px 20px;
    color: #999;
    text-align: center;
  }
  /* 左侧列在移动端占满宽度 */
  .task-left-column,
  .reward-left-column {
    width: 100% !important;
    max-width: 100% !important;
    flex: 0 0 100% !important;
    display: block !important;
    height: auto !important;
    position: absolute !important;
    left: 0 !important;
    right: 0 !important;
    top: 0 !important;
    bottom: 0 !important;
    z-index: 1 !important;
  }

  /* 右侧列在移动端占满宽度 */
  .task-right-column,
  .reward-right-column {
    width: 100% !important;
    max-width: 100% !important;
    flex: 0 0 100% !important;
    display: block !important;
    height: auto !important;
    position: absolute !important;
    left: 0 !important;
    right: 0 !important;
    top: 0 !important;
    bottom: 0 !important;
    z-index: 2 !important;
    transform: translateX(100%) !important;
    transition: transform 0.3s ease !important;
  }

  /* 显示详情时滑动效果 */
  .task-right-column.show-detail,
  .reward-right-column.show-detail {
    transform: translateX(0) !important;
  }

  /* 返回按钮 */
  .mobile-back-button {
    display: flex;
    align-items: center;
    gap: 6px;
    padding: 12px;
    background: #f8f9fa;
    border-bottom: 1px solid #e8e8e8;
    color: #1890ff;
    cursor: pointer;
    font-size: 14px;
    font-weight: 500;
    margin: -16px -16px 16px -16px;
  }

  .mobile-back-button:hover {
    background: #f0f9ff;
  }

  /* 卡片样式调整 */
  .task-sidebar-card,
  .reward-sidebar-card,
  .task-detail-card,
  .reward-detail-card {
    height: 100% !important;
    overflow-y: auto !important;
    border-radius: 0 !important;
    border: none !important;
    box-shadow: none !important;
  }

  .task-sidebar-card,
  .reward-sidebar-card {
    overflow: hidden !important;
  }

  /* 任务详情操作按钮优化 */
  .task-actions {
    display: flex;
    gap: 8px;
    width: 100%;
  }

  .task-actions .ivu-btn {
    flex: 1;
    font-size: 12px;
    padding: 0 4px;
  }

  /* 悬赏信息网格优化 */
  .info-grid {
    display: grid !important;
    grid-template-columns: repeat(2, 1fr) !important;
    gap: 12px !important;
  }

  .info-item {
    display: flex !important;
    flex-direction: column !important;
    align-items: center !important;
    text-align: center !important;
    padding: 8px !important;
    background: #f8f9fa !important;
    border-radius: 6px !important;
  }

  .info-label {
    font-size: 11px !important;
    color: #999 !important;
    margin-top: 4px !important;
  }

  .info-value {
    font-size: 13px !important;
    font-weight: 500 !important;
    margin-top: 2px !important;
    word-break: break-all !important;
  }

  /* 顶部操作栏优化 */
  .modal-header-actions {
    padding: 8px 12px !important;
    display: flex !important;
    flex-direction: column !important;
    gap: 8px !important;
  }

  .modal-header-actions .ivu-btn-group {
    width: 100% !important;
    display: flex !important;
  }

  .modal-header-actions .ivu-btn {
    flex: 1 !important;
  }

  /* 搜索栏优化 */
  .search-bar {
    display: flex !important;
    flex-direction: column !important;
    gap: 8px !important;
  }

  .search-bar .ivu-input {
    width: 100% !important;
    margin-right: 0 !important;
  }

  /* 表格优化 */
  .compact-table {
    font-size: 12px !important;
  }

  /* 表单字段优化 */
  .item-type2 {
    display: block !important;
    margin-bottom: 4px !important;
  }

  .simple-reward-form {
    padding: 12px !important;
  }

  /* 隐藏移动端不需要的元素 */
  .empty-detail,
  .empty-reward-detail {
    display: none !important;
  }

  /* 历练任务嵌入移动端页签时使用自然页面高度，避免十条任务和分页被一屏高度截断。 */
  .task-system-modal,
  .task-container-modal,
  .normal-task-pane,
  .task-layout-row {
    height: auto !important;
    min-height: 0 !important;
    overflow: visible !important;
  }

  .task-system-modal,
  .normal-task-pane {
    display: block !important;
  }

  .task-left-column,
  .task-right-column {
    position: static !important;
    width: 100% !important;
    height: auto !important;
    transform: none !important;
    transition: none !important;
  }

  .task-left-column.mobile-hidden,
  .task-right-column.mobile-hidden {
    display: none !important;
  }

  .task-right-column.mobile-show {
    display: block !important;
  }

  .task-sidebar-card,
  .task-sidebar-card :deep(.ivu-card-body),
  .task-list-container,
  .task-list-scroll {
    height: auto !important;
    min-height: 0 !important;
    max-height: none !important;
    overflow: visible !important;
  }

  .task-list-scroll {
    display: block !important;
    flex: none !important;
  }

  .task-list-pagination {
    position: static !important;
    width: 100% !important;
    margin-top: 4px;
  }
}
/* 非移动端（PC端）保持原有样式 */
@media (min-width: 769px) {
  /* 隐藏移动端元素 */
  .mobile-category-buttons,
  .mobile-reward-category-buttons,
  .mobile-back-button {
    display: none !important;
  }

  /* 显示PC端元素 */
  .pc-category {
    display: block !important;
  }

  /* 保持PC端原有布局 */
  .task-layout-row,
  .reward-layout-row {
    display: flex !important;
    height: 100% !important;
  }

  .task-left-column,
  .reward-left-column,
  .task-right-column,
  .reward-right-column {
    position: static !important;
    transform: none !important;
    height: 100% !important;
  }

  /* 恢复PC端原有样式 */
  .task-sidebar-card,
  .reward-sidebar-card,
  .task-detail-card,
  .reward-detail-card {
    border-radius: 8px !important;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1) !important;
  }

  /* PC端信息网格保持原样 */
  .info-grid {
    display: flex !important;
    flex-direction: column !important;
    gap: 8px !important;
  }

  .info-item {
    display: flex !important;
    align-items: center !important;
    text-align: left !important;
    padding: 0 !important;
    background: transparent !important;
  }
}
/* 顶部Tab样式 */
.top-tabs {
  margin-bottom: 12px;
  background: #fff;
  border-bottom: 1px solid #e8eaec;
}

.task-tabs-top {
  margin: 0;
}

.task-tabs-top :deep(.ivu-tabs-bar) {
  margin-bottom: 0;
  border-bottom: none;
}

.task-tabs-top :deep(.ivu-tabs-nav-container) {
  font-size: 14px;
}

.task-tabs-top :deep(.ivu-tabs-tab) {
  padding: 8px 16px;
}

/* 任务容器 */
.task-container-modal {
  height: calc(100% - 100px);
  overflow: hidden;
}

/* 常规任务样式 */
.normal-task-pane {
  height: 100%;
}

.task-layout-row {
  height: 100%;
}

.task-left-column,
.task-right-column {
  height: 100%;
}

.task-sidebar-card {
  height: 100%;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  border-radius: 8px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}

.task-sidebar-card :deep(.ivu-card-body),
.reward-sidebar-card :deep(.ivu-card-body) {
  height: 100%;
  min-height: 0;
  display: flex;
  flex-direction: column;
  padding: 0;
  overflow: hidden;
}

/* 分类按钮样式 */
.task-category-buttons {
  padding: 12px;
  border-bottom: 1px solid #f0f0f0;
}

.category-row {
  display: flex;
  gap: 8px;
  margin-bottom: 8px;
}

.category-row:last-child {
  margin-bottom: 0;
}

.category-btn {
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  height: 36px;
  border-radius: 6px;
  transition: all 0.3s ease;
}

.category-btn:hover {
  transform: translateY(-1px);
  box-shadow: 0 3px 6px rgba(0, 0, 0, 0.1);
}

/* 任务列表样式 */
.task-list-container {
  flex: 1;
  min-height: 0;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

.task-list-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 12px;
  background: #f8f9fa;
  border-bottom: 1px solid #e8e8e8;
}

.task-count {
  font-size: 12px;
  color: #666;
  font-weight: 500;
}

.uncompleted-count {
  font-size: 12px;
}

.task-list-scroll {
  flex: 1;
  min-height: 0;
  max-height: none;
  overflow-y: auto;
  padding: 8px;
}

.task-list-pagination {
  flex-shrink: 0;
  display: flex;
  justify-content: center;
  padding: 8px 6px;
  background: #fff;
  border-top: 1px solid #e8e8e8;
}

.task-list-item {
  padding: 12px;
  margin-bottom: 8px;
  background: #fafafa;
  border-radius: 8px;
  border: 1px solid #e8e8e8;
  cursor: pointer;
  transition: all 0.3s ease;
}

.task-list-item:hover {
  background: white;
  border-color: #1890ff;
  box-shadow: 0 2px 8px rgba(24, 144, 255, 0.1);
}

.task-list-item.active {
  border-color: #1890ff;
  background: linear-gradient(135deg, #e6f7ff, #f0f9ff);
}

.task-item-header {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 6px;
}

.task-name {
  font-size: 13px;
  font-weight: 500;
  color: #333;
  line-height: 1.4;
  overflow: hidden;
  text-overflow: ellipsis;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
}

.task-item-footer {
  display: flex;
  justify-content: space-between;
  align-items: center;
  font-size: 11px;
}

.status-tag {
  line-height: 18px;
}

.task-level {
  color: #1890ff;
  font-weight: 500;
}

/* 状态颜色 */
.task-list-item.status-0 {
  border-left: 3px solid #666;
}

.task-list-item.status-1 {
  border-left: 3px solid #1890ff;
}

.task-list-item.status-2 {
  border-left: 3px solid #52c41a;
}

.task-list-item.status-9 {
  border-left: 3px solid #ff4d4f;
}

.empty-task-list {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 40px 20px;
  color: #999;
  text-align: center;
}

.empty-task-list p {
  margin-top: 8px;
  font-size: 13px;
}

/* 右侧详情样式 */
.task-detail-card {
  height: 100%;
  border-radius: 8px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}

.task-detail-content {
  height: 100%;
  overflow-y: auto;
  padding: 16px;
}
.task-detail-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 20px;
  padding-bottom: 16px;
  border-bottom: 1px solid #f0f0f0;
  flex-wrap: wrap;
  gap: 12px;
}

.task-title-section {
  flex: 1;
  min-width: 0; /* 防止标题过长时挤压 */
}

.task-detail-title {
  margin: 0 0 8px 0;
  font-size: 18px;
  color: #333;
  display: flex;
  align-items: center;
  gap: 8px;
  line-height: 1.4;
  word-break: break-word; /* 长标题换行 */
}

.task-tags {
  display: flex;
  gap: 6px;
  flex-wrap: wrap;
}

/* 任务操作按钮容器 */
.task-actions-wrapper {
  flex-shrink: 0;
}

.task-actions {
  display: flex;
  gap: 8px;
  align-items: flex-start;
  flex-wrap: wrap;
}
/*.task-detail-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 20px;
  padding-bottom: 16px;
  border-bottom: 1px solid #f0f0f0;
}

.task-title-section {
  flex: 1;
}

.task-detail-title {
  margin: 0 0 8px 0;
  font-size: 18px;
  color: #333;
  display: flex;
  align-items: center;
  gap: 8px;
}

.task-tags {
  display: flex;
  gap: 6px;
  flex-wrap: wrap;
}

.task-actions {
  display: flex;
  gap: 8px;
  align-items: flex-start;
}*/

/* 任务描述 */
.task-description {
  margin-bottom: 16px;
}

.section-title {
  display: flex;
  align-items: center;
  gap: 8px;
  margin: 16px 0 8px 0;
  color: #1890ff;
  font-size: 14px;
  font-weight: 500;
}

.description-content {
  line-height: 1.6;
  color: #555;
  font-size: 13px;
  text-indent: 2em;
}

/* 等级限制 */
.task-level-info {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 8px 12px;
  background: #f0f8ff;
  border-radius: 4px;
  margin-bottom: 16px;
  font-size: 12px;
  color: #1890ff;
}

/* 任务要求 */
.requirements-list {
  margin-top: 8px;
}

.requirement-item {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 10px;
  margin-bottom: 8px;
  background: #fafafa;
  border-radius: 6px;
  border: 1px solid #e8e8e8;
  transition: all 0.3s ease;
}

.requirement-item:hover {
  background: white;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.08);
}

.requirement-item.completed {
  background: linear-gradient(135deg, #f6ffed, #f9ffe6);
  border-color: #b7eb8f;
}

.requirement-icon {
  flex-shrink: 0;
}

.requirement-icon .ivu-icon {
  font-size: 18px;
}

.requirement-content {
  flex: 1;
  min-width: 0;
}

.requirement-name {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 4px;
  font-size: 13px;
  color: #333;
  font-weight: 500;
}

.submit-btn {
  color: #1890ff;
  cursor: pointer;
  font-size: 12px;
  display: flex;
  align-items: center;
  gap: 3px;
  transition: all 0.3s ease;
}

.submit-btn:hover {
  color: #096dd9;
}

.requirement-progress {
  display: flex;
  align-items: center;
  gap: 8px;
}

.progress-text {
  font-size: 12px;
  color: #666;
}

/* 提交材料面板 */
.material-submit-panel {
  margin-top: 12px;
}

.material-submit-content {
  padding: 12px;
  background: #f8f9fa;
  border-radius: 6px;
  border: 1px solid #e8e8e8;
}

.form-group {
  margin: 12px 0;
}

.form-group label {
  display: block;
  margin-bottom: 4px;
  font-size: 12px;
  color: #333;
}

.submit-buttons {
  display: flex;
  justify-content: flex-end;
  margin-top: 16px;
}

/* 任务奖励 */
.rewards-list {
  margin-top: 8px;
}

.appointed-rewards {
  margin-bottom: 12px;
}
.reward-tip {
  margin-bottom: 8px;
  font-size: 12px;
  color: #666;
}

.reward-radio-group {
  width: 100%;
}

.reward-option {
  margin-bottom: 6px;
  padding: 8px;
  border-radius: 6px;
  border: 1px solid #e8e8e8;
  transition: all 0.3s ease;
}

.reward-option:hover {
  border-color: #1890ff;

}
.reward-radio-item {
  display: flex;
  align-items: stretch;
  margin-bottom: 8px;
}
.reward-item {
  display: flex;
  align-items: center;
  gap: 8px;
}

.reward-item.fixed {
  padding: 2px;
  margin-bottom: 6px;
  background: #fafafa;
  border-radius: 6px;
  border: 1px solid #e8e8e8;
}

.reward-item .ivu-icon {
  font-size: 16px;
}

.reward-name {
  font-size: 13px;
  font-weight: 500;
  color: #333;
  flex: 1;
}

.reward-type {
  font-size: 11px;
  color: #999;
  background: #f0f0f0;
  padding: 1px 6px;
  border-radius: 3px;
}

.reward-info {
  flex: 1;
}

.reward-details {
  display: flex;
  align-items: center;
  gap: 6px;
  margin-top: 2px;
}

.sect-contribution {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  margin-left: 12px;
  font-size: 12px;
  color: #52c41a;
  font-weight: 500;
}

/* 空状态 */
.empty-detail {
  display: flex;
  align-items: center;
  justify-content: center;
  height: 100%;
}

.empty-content {
  text-align: center;
  color: #999;
}

.empty-content h3 {
  margin: 12px 0 6px 0;
  font-size: 16px;
  color: #666;
}

.empty-content p {
  font-size: 13px;
}

/* 悬赏任务样式 */
.reward-task-pane {
  height: 100%;
}

.reward-layout-row {
  height: 100%;
}

.reward-left-column,
.reward-right-column {
  height: 100%;
}

.reward-sidebar-card {
  height: 100%;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  border-radius: 8px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}

/* 悬赏分类按钮 */
.reward-category-buttons {
  padding: 12px;
  border-bottom: 1px solid #f0f0f0;
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.reward-category-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  height: 36px;
  border-radius: 6px;
  transition: all 0.3s ease;
}

.reward-category-btn:hover {
  transform: translateY(-1px);
  box-shadow: 0 3px 6px rgba(0, 0, 0, 0.1);
}

/* 我的悬赏列表 */
.reward-list-container {
  flex: 1;
  min-height: 0;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

.reward-list-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 12px;
  background: #f8f9fa;
  border-bottom: 1px solid #e8e8e8;
}

.reward-count {
  font-size: 12px;
  color: #666;
  font-weight: 500;
}

.reward-list-scroll {
  flex: 1;
  min-height: 0;
  max-height: none;
  overflow-y: auto;
  padding: 8px;
}

.reward-section {
  margin-bottom: 12px;
}

.section-title-small {
  font-size: 11px;
  color: #999;
  margin-bottom: 6px;
  padding-left: 4px;
  text-transform: uppercase;
}

.reward-list-item {
  padding: 10px;
  margin-bottom: 6px;
  background: #fafafa;
  border-radius: 6px;
  border: 1px solid #e8e8e8;
  cursor: pointer;
  transition: all 0.3s ease;
}

.reward-list-item:hover {
  background: white;
  border-color: #1890ff;
  box-shadow: 0 2px 6px rgba(24, 144, 255, 0.1);
}

.reward-list-item.active {
  border-color: #1890ff;
  background: linear-gradient(135deg, #e6f7ff, #f0f9ff);
}

.reward-list-item.completed {
  opacity: 0.7;
}

.reward-item-header {
  display: flex;
  align-items: center;
  gap: 6px;
  margin-bottom: 4px;
}

.reward-name {
  font-size: 12px;
  font-weight: 500;
  color: #333;
  line-height: 1.4;
  overflow: hidden;
  text-overflow: ellipsis;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
}

.reward-item-footer {
  display: flex;
  justify-content: flex-start;
}

.empty-reward-list {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 40px 20px;
  color: #999;
  text-align: center;
}

.empty-reward-list p {
  margin-top: 8px;
  font-size: 13px;
}

/* 发布悬赏侧边栏 */
.publish-reward-sidebar {
  flex: 1;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

.form-header {
  padding: 12px;
  border-bottom: 1px solid #f0f0f0;
}

.form-title {
  font-size: 14px;
  color: #333;
  margin: 0;
  display: flex;
  align-items: center;
  gap: 6px;
}

.simple-reward-form {
  flex: 1;
  overflow-y: auto;
  padding: 12px;
}

.simple-reward-form :deep(.ivu-form-item) {
  margin-bottom: 12px;
}

/* 悬赏详情卡片 */
.reward-detail-card {
  height: 100%;
  border-radius: 8px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}

.reward-detail-content {
  height: 100%;
  overflow-y: auto;
  padding: 16px;
}

/* 发布悬赏详情 */
.publish-reward-detail {
  padding: 16px;
}

.form-detail-header {
  margin-bottom: 20px;
  padding-bottom: 16px;
  border-bottom: 1px solid #f0f0f0;
}

.form-desc {
  margin-top: 4px;
  font-size: 13px;
  color: #666;
}

.reward-rules-detail {
  margin-top: 20px;
  padding: 12px;
  background: #f8f9fa;
  border-radius: 6px;
}

.reward-rules-detail h4 {
  margin: 0 0 8px 0;
  font-size: 14px;
  color: #333;
}

.reward-rules-detail p {
  margin: 4px 0;
  font-size: 12px;
  color: #666;
  line-height: 1.5;
}

/* 悬赏列表详情 */
.reward-list-detail {
  padding: 16px;
}

.list-detail-header {
  margin-bottom: 20px;
  padding-bottom: 16px;
  border-bottom: 1px solid #f0f0f0;
}

/* 空状态 */
.empty-reward-detail {
  display: flex;
  align-items: center;
  justify-content: center;
  height: 100%;
}

/* 滚动条优化 */
.task-list-scroll::-webkit-scrollbar,
.reward-list-scroll::-webkit-scrollbar,
.task-detail-content::-webkit-scrollbar,
.reward-detail-content::-webkit-scrollbar {
  width: 4px;
}

.task-list-scroll::-webkit-scrollbar-track,
.reward-list-scroll::-webkit-scrollbar-track,
.task-detail-content::-webkit-scrollbar-track,
.reward-detail-content::-webkit-scrollbar-track {
  background: #f1f1f1;
}

.task-list-scroll::-webkit-scrollbar-thumb,
.reward-list-scroll::-webkit-scrollbar-thumb,
.task-detail-content::-webkit-scrollbar-thumb,
.reward-detail-content::-webkit-scrollbar-thumb {
  background: #c1c1c1;
  border-radius: 2px;
}

.task-list-scroll::-webkit-scrollbar-thumb:hover,
.reward-list-scroll::-webkit-scrollbar-thumb:hover,
.task-detail-content::-webkit-scrollbar-thumb:hover,
.reward-detail-content::-webkit-scrollbar-thumb:hover {
  background: #a8a8a8;
}

/* 响应式设计 */
@media (max-width: 1200px) {
  .task-left-column,
  .task-right-column,
  .reward-left-column,
  .reward-right-column {
    span: 24;
  }
}

.item-name {
  font-weight: 500;
}

.item-count {
  color: #1890ff;
  font-weight: bold;
}

.item-type {
  color: #999;
  font-size: 12px;
}
.item-type2 {
  color: #999;
  font-size: 14px;
}
</style>

<style>
/* 全局样式调整 */
.task-modal .ivu-modal-body {
  padding: 16px;
  max-height: 1140px;
  overflow-x: hidden;
  overflow-y: hidden;
}

.nested-modal .ivu-modal-body {
  padding: 16px;
}
</style>

<style>
/* Compact task rows and clearer detail hierarchy. */
.task-system-modal .task-list-scroll,
.task-system-modal .reward-list-scroll { padding: 5px 7px; }
.task-system-modal .task-list-item,
.task-system-modal .reward-list-item {
  min-height: 0;
  margin: 0 0 4px;
  padding: 7px 8px;
  border-radius: 4px;
}
.task-system-modal .task-item-header,
.task-system-modal .reward-item-header {
  display: flex;
  align-items: center;
  gap: 7px;
  margin-bottom: 4px;
}
.task-system-modal .task-item-header .ivu-icon,
.task-system-modal .reward-item-header .ivu-icon { flex: 0 0 auto; }
.task-system-modal .task-name,
.task-system-modal .reward-name {
  display: block;
  min-width: 0;
  flex: 1 1 auto;
  overflow: hidden;
  font-size: 15px;
  font-weight: 400;
  line-height: 1.3;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.task-system-modal .status-tag--inline {
  flex: 0 0 auto;
  margin-left: auto;
  white-space: nowrap;
}
.task-system-modal .task-item-footer,
.task-system-modal .reward-item-footer {
  display: flex;
  justify-content: flex-end;
  min-height: 20px;
  font-size: 10px;
}
.task-system-modal .status-tag {
  margin: 0;
  padding: 1px 5px;
  border-radius: 3px;
  font-size: 10px;
  line-height: 16px;
}
.task-system-modal .task-detail-content,
.task-system-modal .reward-detail-content {
  padding: 14px 16px 22px;
}
.task-system-modal .task-detail-header,
.task-system-modal .reward-detail-header {
  align-items: center;
  margin-bottom: 12px;
  padding-bottom: 11px;
  gap: 10px;
}
.task-system-modal .task-detail-title,
.task-system-modal .reward-detail-title {
  margin-bottom: 6px;
  font-size: 21px;
  font-weight: 800;
  line-height: 1.3;
}
.task-system-modal .task-tags { gap: 4px; }
.task-system-modal .task-description,
.task-system-modal .task-requirements,
.task-system-modal .task-rewards { margin-bottom: 12px; }
.task-system-modal .section-title {
  margin: 11px 0 6px;
  padding-left: 7px;
  font-size: 13px;
  font-weight: 700;
}
.task-system-modal .description-content {
  padding: 9px 11px;
  color: #54738d;
  background: #f7fbff;
  border: 1px solid #dbe8f2;
  border-radius: 4px;
  font-size: 12px;
  line-height: 1.55;
  text-indent: 0;
}
.task-system-modal .task-level-info {
  margin-bottom: 11px;
  padding: 7px 10px;
  border-radius: 4px;
}
.task-system-modal .requirements-list,
.task-system-modal .rewards-list { margin-top: 5px; }
.task-system-modal .requirement-item {
  gap: 9px;
  margin-bottom: 5px;
  padding: 8px 9px;
  border-radius: 4px;
}
.task-system-modal .requirement-name { font-size: 12px; }
.task-system-modal .progress-text { color: #6f8da6; font-size: 11px; }
.task-system-modal .rewards-list .reward-item.fixed,
.task-system-modal .reward-option {
  margin-bottom: 5px;
  padding: 7px 9px;
  border-radius: 4px;
}
.task-system-modal .reward-name { font-size: 12px; }
.task-system-modal .task-actions {
  gap: 5px;
}
@media (max-width: 768px) {
  .task-system-modal .task-name,
  .task-system-modal .reward-name { font-size: 14px; }
  .task-system-modal .task-detail-title,
  .task-system-modal .reward-detail-title { font-size: 18px; }
  .task-system-modal .task-detail-header,
  .task-system-modal .reward-detail-header { align-items: flex-start; }
}
</style>

<style>
/* Task workbench visual system. Existing actions and data bindings stay unchanged. */
.task-system-modal {
  min-height: 0;
  padding: 12px;
  color: #315a7c;
  background: #f5f9fc;
}
.task-workbench-head {
  display: flex;
  align-items: center;
  gap: 14px;
  margin-bottom: 10px;
  padding: 10px 12px;
  background: #ffffff;
  border: 1px solid #c9dced;
  border-radius: 6px;
}
.task-workbench-title {
  display: grid;
  gap: 2px;
  min-width: 92px;
}
.task-workbench-kicker {
  color: #8aa4b8;
  font-size: 10px;
}
.task-workbench-title strong {
  color: #315a7c;
  font-size: 17px;
}
.task-workbench-stats {
  display: flex;
  gap: 6px;
  min-width: 0;
}
.task-stat {
  display: inline-flex;
  align-items: baseline;
  gap: 6px;
  min-width: 76px;
  padding: 5px 8px;
  background: #f7fbff;
  border: 1px solid #dbe8f2;
  border-radius: 4px;
}
.task-stat small { color: #7895aa; font-size: 10px; }
.task-stat b { color: #3978b7; font-size: 14px; }
.task-stat--active { background: #eef6fd; border-color: #b9d3e6; }
.task-stat--active b { color: #3978b7; }
.task-stat--ready { background: #eef8f3; border-color: #b9d9c8; }
.task-stat--ready b { color: #4d9b78; }
.task-workbench-refresh { margin-left: auto; }
.task-system-modal .top-tabs {
  margin-bottom: 10px;
  padding: 0 8px;
  background: #ffffff;
  border: 1px solid #c9dced;
  border-radius: 6px;
}
.task-system-modal .task-container-modal { min-height: 0; }
.task-system-modal .task-layout-row,
.task-system-modal .reward-layout-row { margin-left: -5px; margin-right: -5px; }
.task-system-modal .task-left-column,
.task-system-modal .task-right-column,
.task-system-modal .reward-left-column,
.task-system-modal .reward-right-column { padding-left: 5px; padding-right: 5px; }
.task-system-modal .task-sidebar-card,
.task-system-modal .task-detail-card,
.task-system-modal .reward-sidebar-card,
.task-system-modal .reward-detail-card {
  background: #ffffff;
  border: 1px solid #c9dced;
  border-radius: 6px;
  box-shadow: none;
}
.task-system-modal .task-category-buttons,
.task-system-modal .mobile-category-buttons,
.task-system-modal .mobile-reward-category-buttons {
  padding: 8px;
  background: #f7fbff;
  border-bottom: 1px solid #dbe8f2;
}
.task-system-modal .category-row { gap: 5px; }
.task-system-modal .category-btn,
.task-system-modal .mobile-reward-category-btn {
  min-height: 30px;
  padding: 5px 9px;
  color: #426581;
  background: #ffffff;
  border: 1px solid #c9dced;
  border-radius: 4px;
  box-shadow: none;
}
.task-system-modal .category-btn:hover,
.task-system-modal .mobile-reward-category-btn:hover {
  color: #315a7c;
  background: #eef6fd;
  border-color: #7fa9ca;
}
.task-system-modal .category-btn.ivu-btn-primary,
.task-system-modal .mobile-reward-category-btn.ivu-btn-primary {
  color: #ffffff;
  background: #3978b7;
  border-color: #2f669d;
}
.task-system-modal .task-list-header,
.task-system-modal .reward-list-header {
  padding: 10px 12px;
  color: #6f8da6;
  background: #ffffff;
  border-bottom: 1px solid #e4eef5;
}
.task-system-modal .task-list-item,
.task-system-modal .reward-list-item {
  margin: 0 8px;
  padding: 10px 9px;
  background: #ffffff;
  border: 0;
  border-bottom: 1px solid #e6eff5;
  border-radius: 0;
  transition: background .16s, box-shadow .16s;
}
.task-system-modal .task-list-item:hover,
.task-system-modal .reward-list-item:hover {
  background: #f7fbff;
}
.task-system-modal .task-list-item.active,
.task-system-modal .reward-list-item.active {
  background: #eef6fd;
  box-shadow: inset 3px 0 #3978b7;
}
.task-system-modal .task-list-item,
.task-system-modal .task-list-item.status-0,
.task-system-modal .task-list-item.status-1,
.task-system-modal .task-list-item.status-2,
.task-system-modal .task-list-item.status-9 {
  border-left: 3px solid transparent;
}
.task-system-modal .task-list-item.active {
  border-left-color: #3978b7;
  box-shadow: none;
}
.task-system-modal .task-name,
.task-system-modal .reward-name { color: #315a7c; font-weight: 400; }
.task-system-modal .task-level,
.task-system-modal .task-count { color: #7895aa; }
.task-system-modal .task-detail-card,
.task-system-modal .reward-detail-card { padding: 0; }
.task-system-modal .task-detail-content,
.task-system-modal .reward-detail-content { padding: 16px; }
.task-system-modal .task-detail-header,
.task-system-modal .reward-detail-header {
  padding-bottom: 12px;
  border-bottom-color: #dbe8f2;
}
.task-system-modal .task-detail-title,
.task-system-modal .reward-detail-title { color: #315a7c; }
.task-system-modal .section-title {
  color: #426581;
  border-left-color: #3978b7;
}
.task-system-modal .requirement-item,
.task-system-modal .reward-item,
.task-system-modal .task-level-info {
  background: #f7fbff;
  border-color: #c9dced;
}
.task-system-modal .requirement-item.completed { background: #eef8f3; border-color: #b9d9c8; }
.task-system-modal .requirements-list,
.task-system-modal .rewards-list { gap: 6px; }
.task-system-modal .task-actions .ivu-btn,
.task-system-modal .task-actions-wrapper .ivu-btn { border-radius: 4px; }
.task-system-modal .mobile-back-button {
  color: #3978b7;
  border-bottom-color: #dbe8f2;
}
@media (max-width: 768px) {
  .task-system-modal { padding: 8px 6px; }
  .task-workbench-head { flex-wrap: wrap; gap: 8px; padding: 9px; }
  .task-workbench-title { flex: 1 1 90px; }
  .task-workbench-stats { order: 3; width: 100%; }
  .task-stat { flex: 1 1 0; justify-content: center; min-width: 0; }
  .task-workbench-refresh { margin-left: 0; }
  .task-system-modal .task-detail-content,
  .task-system-modal .reward-detail-content { padding: 12px 10px; }
}
</style>



// WEBPACK FOOTER //
// src/components/TaskSystemModal.vue